import { useEffect, useMemo, useState } from "react";
import { useFilterOptions } from "@/features/attorney/views/Entries/filters/useFilterOptions";
import { useAttorneyFilters } from "@/features/attorney/views/Entries/filters/useFilters";
import { useConfiguredTimekeepers } from "@/features/attorney/views/Entries/timekeepers";
import {
	adjacentCalendarYearRanges,
	calendarSummaryToDailySums,
	calendarYearRange,
	useCalendarSummary,
} from "@/shared/hooks/useEntryData";
import { TimekeeperFilterPopover } from "@/v2/components/TimekeeperFilterPopover";
import { useDateParams } from "@/v2/hooks/useDateParams";
import Calendar from "./Calendar";

const EMPTY_FILTER_OPTION_ENTRIES = [] as const;

/** Thin wrapper that connects the Calendar component to entry summary data. */
export default function CalendarPage() {
	const { date } = useDateParams();
	const timekeepers = useConfiguredTimekeepers();
	const fieldOptions = useFilterOptions(
		EMPTY_FILTER_OPTION_ENTRIES,
		timekeepers,
	);
	const { filters, defaultFilters, setTimekeepers } =
		useAttorneyFilters(fieldOptions);
	const [viewedMonth, setViewedMonth] = useState(
		() => new Date(date.getFullYear(), date.getMonth(), 1),
	);

	useEffect(() => {
		setViewedMonth(new Date(date.getFullYear(), date.getMonth(), 1));
	}, [date]);

	const calendarRange = useMemo(
		() => calendarYearRange(viewedMonth),
		[viewedMonth],
	);
	const prefetchRanges = useMemo(
		() => adjacentCalendarYearRanges(viewedMonth),
		[viewedMonth],
	);
	const { data: calendarDays = [] } = useCalendarSummary(
		calendarRange,
		prefetchRanges,
		filters.timekeeper,
	);
	const dailySums = useMemo(
		() => calendarSummaryToDailySums(calendarDays),
		[calendarDays],
	);
	return (
		<Calendar
			dailySums={dailySums}
			viewedMonth={viewedMonth}
			onViewedMonthChange={setViewedMonth}
			filterSlot={
				<TimekeeperFilterPopover
					timekeepers={timekeepers}
					selectedIds={filters.timekeeper ?? []}
					defaultIds={defaultFilters.timekeeper ?? []}
					onChange={setTimekeepers}
				/>
			}
		/>
	);
}
